import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import WhatsAppButton from '../components/WhatsAppButton';

const contentMap = {
  hospitals: {
    title: "Healing Through Memories",
    description: "From maternity to recovery journeys, hospitals can offer personalized photobooks that nurture deeper emotional bonds.",
    whyItWorks: [
      "Enhance patient experience",
      "Create emotional connections",
      "Differentiate your facility"
    ],
    useCases: "Birth memories, recovery milestones, family support, patient journey documentation",
    cta: "Elevate patient care"
  },
  events: {
    title: "From Event to Everlasting",
    description: "Extend the impact of your event with personalized take-home photobooks guests create instantly.",
    whyItWorks: [
      "Extend event engagement",
      "Create shareable moments",
      "Boost sponsor visibility"
    ],
    useCases: "Conferences, weddings, festivals, brand activations, product launches",
    cta: "Make your event unforgettable"
  },
  corporate: {
    title: "Gifting with Meaning",
    description: "Enhance HR, internal comms, and client gifting with emotional and personalized photobooks.",
    whyItWorks: [
      "Strengthen company culture",
      "Enhance client relationships",
      "Boost employee engagement"
    ],
    useCases: "Onboarding, milestones, team building, client appreciation, corporate events",
    cta: "Build stronger connections"
  },
  education: {
    title: "Celebrating Learning Journeys",
    description: "Help students and parents preserve treasured school memories with customizable photobooks they'll cherish.",
    whyItWorks: [
      "School-branded templates",
      "Easy QR/Voucher redemption",
      "Revenue share with zero cost to school"
    ],
    useCases: "Yearbooks, graduation memory books, alumni events",
    cta: "Capture the school years"
  },
  fnb: {
    title: "Beyond the Plate",
    description: "Reinforce your dining experience with printed keepsakes linked to meals, events, or loyalty programs.",
    whyItWorks: [
      "Create loyalty-driven experiences",
      "Drive campaign engagement",
      "Memorable packaging integration"
    ],
    useCases: "Date night photobooks, birthday club, drink-and-memory promos",
    cta: "Serve more than food"
  },
  hotels: {
    title: "Your Stay, Their Story",
    description: "Surprise and delight hotel guests with personalized vacation photobooks they can print and take home.",
    whyItWorks: [
      "Boost Guest Experience",
      "Drive Repeat Bookings",
      "Promote Visual Brand Story"
    ],
    useCases: "Loyalty tiers, room upsells, honeymoon packages",
    cta: "Let memories travel home"
  },
  lifestyle: {
    title: "Style Meets Story",
    description: "Convert store visits into emotional brand connections through print-on-demand photo experiences.",
    whyItWorks: [
      "Boost foot traffic and dwell time",
      "Drive social media and UGC",
      "Support influencer activations"
    ],
    useCases: "Style photobooks, pop-up campaigns, milestone loyalty rewards",
    cta: "Let your store live on their shelf"
  },
  malls: {
    title: "Make the Mall Memorable",
    description: "Turn holiday shoppers into loyal fans with seasonal and event-driven photo book campaigns.",
    whyItWorks: [
      "Enhance seasonal activations",
      "Monetize footfall with partner tenants",
      "Integrate into decoration zones"
    ],
    useCases: "Raya/Christmas booths, tenant promos, loyalty program gifts",
    cta: "Make your mall unforgettable"
  },
  tourism: {
    title: "From Journey to Journal",
    description: "Empower tourists to create beautiful printed stories of their travels, while promoting your destination.",
    whyItWorks: [
      "Highlight destinations with templates",
      "Promote loyalty and sharing",
      "Easy airport or hotel redemption"
    ],
    useCases: "Airport campaigns, bundled with attractions or tours",
    cta: "Print the best of their trip"
  },
  places: {
    title: "Capture the Wonder",
    description: "Theme parks, museums, and attractions come to life when guests can print their favorite moments instantly.",
    whyItWorks: [
      "High family engagement",
      "Branded collectibles",
      "Educational reinforcement"
    ],
    useCases: "Exhibit memory books, mascot photobooks, kids activities",
    cta: "Make the visit last forever"
  },
  nightlife: {
    title: "Capture the Rhythm",
    description: "Preserve the electric atmosphere of nightlife with vibrant photobooks that let guests relive their dance floor memories.",
    whyItWorks: [
      "Enhance nightlife experience",
      "Create shareable memories",
      "Boost social media presence"
    ],
    useCases: "Club events, DJ nights, special celebrations",
    cta: "Keep the party going"
  },
  nonprofit: {
    title: "Stories That Inspire Change",
    description: "Amplify your mission and donor engagement with meaningful photobooks that showcase impact stories and community connections.",
    whyItWorks: [
      "Showcase impact visually",
      "Strengthen donor relationships",
      "Create lasting fundraising materials"
    ],
    useCases: "Annual reports, donor appreciation, volunteer recognition, campaign materials, event documentation",
    cta: "Turn stories into support"
  }
};

// Map industry names to their photo folder names
const industryPhotoMap = {
  hospitals: 'Hospitals & Healthcare',
  events: 'Events & Exhibitions ',
  corporate: 'Corporate & Enterprise',
  education: 'Education',
  fnb: 'F&B',
  hotels: 'Hotel',
  lifestyle: 'Lifestyle Retail',
  malls: 'Malls & Marketplaces',
  tourism: 'Travel Tourism',
  places: 'Places of Interest',
  nightlife: 'Events & Exhibitions ', // Using events photos for nightlife as fallback
  nonprofit: 'Corporate & Enterprise' // Using corporate photos as fallback for nonprofit
};

// Map to specific hero images for each industry
const industryHeroImages = {
  hospitals: encodeURI('/Photos for Thumbnails_250819/Hospitals & Healthcare/194132170_l.jpg'),
  events: encodeURI('/Photos for Thumbnails_250819/Events & Exhibitions /205736134_l.jpg'),
  corporate: encodeURI('/Photos for Thumbnails_250819/Corporate & Enterprise/235399802_l.jpg'),
  education: encodeURI('/Photos for Thumbnails_250819/Education/161133486_l.jpg'),
  fnb: encodeURI('/Photos for Thumbnails_250819/F&B/243773152_l.jpg'),
  hotels: encodeURI('/Photos for Thumbnails_250819/Hotel/155883784_l.jpg'),
  lifestyle: encodeURI('/Photos for Thumbnails_250819/Lifestyle Retail/206475442_l.jpg'),
  malls: encodeURI('/Photos for Thumbnails_250819/Malls & Marketplaces/157201568_l.jpg'),
  tourism: encodeURI('/Photos for Thumbnails_250819/Travel Tourism/131184682_l.jpg'),
  places: encodeURI('/Photos for Thumbnails_250819/Places of Interest/97640914_l.jpg'),
  nightlife: encodeURI('/Photos for Thumbnails_250819/Events & Exhibitions /213939337_l.jpg'),
  nonprofit: encodeURI('/Photos for Thumbnails_250819/Corporate & Enterprise/235399802_l.jpg')
};

function    IndustryPage() {
  const [industry, setIndustry] = useState('');
  const [content, setContent] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    const path = window.location.pathname;
    const industryFromPath = path.split('/')[2]; // Get the industry from /industry/{industry}
    setIndustry(industryFromPath);
    setContent(contentMap[industryFromPath]);
    
    // Preload the hero image
    const heroImage = industryHeroImages[industryFromPath];
    if (heroImage) {
      const img = new Image();
      img.onload = () => setImageLoaded(true);
      img.src = heroImage;
    }
  }, []);

  if (!content) return <div className="p-6">Industry not found.</div>;

  // Format industry name for display
  const formatIndustryName = (name) => {
    if (name === 'fnb') return 'F&B';
    if (name === 'places') return 'Places of Interest';
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  const industryName = formatIndustryName(industry);
  const heroImage = industryHeroImages[industry];
  
  // Debug statements removed for security
  // console.log('Industry:', industry);
  // console.log('Hero Image Path:', heroImage);
  
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Industry-Specific Image */}
      <section 
        className="relative py-20 min-h-[500px] flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${heroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed' // Optional: creates parallax effect but may impact performance
        }}
      >
        {/* Loading placeholder while image loads */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 opacity-20"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <motion.h1 
              className="text-4xl md:text-5xl font-display font-bold mb-6 text-white"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {content.title}
            </motion.h1>
            <motion.p 
              className="text-xl mb-8 text-gray-100"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {content.description}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <Link to="/contact" className="btn-primary">Book A Demo</Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why It Works Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title">Why It Works for {industryName}</h2>
            <p className="section-subtitle">Discover how our solutions can transform your {industryName.toLowerCase()} experience</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {content.whyItWorks && content.whyItWorks.map((item, index) => (
              <div key={index} className="card p-8">
                <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                  <span className="text-primary-600 text-2xl font-bold">{index + 1}</span>
                </div>
                <h3 className="text-xl font-semibold mb-3">{item}</h3>
                <p className="text-gray-600">Our photobook solution helps you {item.toLowerCase()} through personalized brand experiences.</p>
              </div>
            ))}
          </div>

          {/* Use Cases */}
          <div className="mt-16 bg-white p-8 rounded-xl text-center">
            <h3 className="text-2xl font-semibold mb-4">Popular Use Cases</h3>
            <p className="text-gray-700 mb-6">{content.useCases}</p>
            
            <div className="flex flex-wrap gap-3 justify-center">
              {content.useCases.split(", ").map((useCase, index) => (
                <span key={index} className="bg-primary-100 text-primary-700 px-4 py-2 rounded-full text-sm font-medium">
                  {useCase}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Case Study Section
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title">Success Story</h2>
            <p className="section-subtitle">Real results from our {industryName.toLowerCase()} clients</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-lg text-gray-600 mb-6">See how a leading {industryName.toLowerCase()} business transformed their customer experience with our photobook solution.</p>
              
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 mb-6">
                <p className="text-gray-600 italic mb-4">"Implementing Moments by Photobook has been a game-changer for our business. Our customers love the personalized keepsakes, and we've seen a 30% increase in repeat visits."</p>
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                    <span className="text-primary-600 font-bold">C</span>
                  </div>
                  <div>
                    <h4 className="font-semibold">Client Name</h4>
                    <p className="text-sm text-gray-500">{industryName} Director</p>
                  </div>
                </div>
              </div>
              
              <ul className="space-y-3">
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-green-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>30% increase in customer satisfaction</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-green-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>25% boost in social media mentions</span>
                </li>
                <li className="flex items-start">
                  <svg className="h-6 w-6 text-green-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Significant improvement in brand loyalty</span>
                </li>
              </ul>
            </div>
            
            <div>
              <img src="/images/case-study.svg" alt="Case study" className="rounded-xl shadow-lg w-full h-auto" />
            </div>
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-500 to-primary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-6">{content.cta}</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">Contact us today to discuss how our photobook solutions can help your {industryName.toLowerCase()} business create lasting memories.</p>
          <Link to="/contact" className="btn-lg bg-white text-primary-600 hover:bg-gray-100 rounded-lg">Book A Demo</Link>
        </div>
      </section>
      <WhatsAppButton />
    </div>
  );
}

export default IndustryPage;
